import { FormEvent, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Shell } from '../components/Layout.js';
import { useAuth } from '../state/auth.js';
import { api, ApiError } from '../api/client.js';
import { useDocumentMeta } from '../lib/document-meta.js';
import { useT } from '../state/i18n.js';

type Tone = 'ok' | 'err';

export function AccountPage(): JSX.Element {
  const t = useT();
  useDocumentMeta({ title: t({ ko: '계정', en: 'Account' }), noindex: true });
  const auth = useAuth();
  const navigate = useNavigate();

  if (auth.loading) {
    return (
      <Shell>
        <section className="account">
          <p className="muted">{t({ ko: '불러오는 중…', en: 'Loading…' })}</p>
        </section>
      </Shell>
    );
  }
  if (!auth.user) return <Navigate to="/login" replace />;

  return (
    <Shell>
      <section className="account">
        <header>
          <h1>{t({ ko: '계정', en: 'Account' })}</h1>
          <p className="muted">
            {t({
              ko: '프로필, 비밀번호, 계정 접근 권한을 관리하세요.',
              en: 'Manage your profile, password, and account access.',
            })}
          </p>
        </header>
        <ProfileCard />
        <EmailVerificationCard />
        <PasswordCard />
        <DangerCard onDeleted={() => navigate('/', { replace: true })} />
      </section>
    </Shell>
  );
}

function StatusLine({ status }: { status: { tone: Tone; text: string } | null }): JSX.Element | null {
  if (!status) return null;
  return <p className={`status ${status.tone}`}>{status.text}</p>;
}

function ProfileCard(): JSX.Element {
  const t = useT();
  const auth = useAuth();
  const [name, setName] = useState(auth.user?.name ?? '');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ tone: Tone; text: string } | null>(null);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setStatus(null);
    try {
      await api.updateProfile(name.trim());
      await auth.refresh();
      setStatus({ tone: 'ok', text: t({ ko: '프로필이 업데이트되었습니다.', en: 'Profile updated.' }) });
    } catch (err) {
      setStatus({
        tone: 'err',
        text: err instanceof Error ? err.message : t({ ko: '업데이트에 실패했습니다.', en: 'Update failed.' }),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="account-card">
      <h2>{t({ ko: '프로필', en: 'Profile' })}</h2>
      <form onSubmit={submit}>
        <label>
          {t({ ko: '표시 이름', en: 'Display name' })}
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={120}
          />
        </label>
        <label>
          {t({ ko: '이메일', en: 'Email' })}
          <input value={auth.user?.email ?? ''} readOnly disabled />
        </label>
        <div className="card-actions">
          <button type="submit" className="cta-primary" disabled={busy}>
            {busy ? t({ ko: '저장 중…', en: 'Saving…' }) : t({ ko: '변경사항 저장', en: 'Save changes' })}
          </button>
        </div>
        <StatusLine status={status} />
      </form>
    </article>
  );
}

function EmailVerificationCard(): JSX.Element {
  const t = useT();
  const auth = useAuth();
  const verified = auth.user?.emailVerified ?? false;
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ tone: Tone; text: string } | null>(null);

  async function resend(): Promise<void> {
    setBusy(true);
    setStatus(null);
    try {
      const result = await api.requestVerification();
      if (result.alreadyVerified) {
        await auth.refresh();
        setStatus({ tone: 'ok', text: t({ ko: '이미 인증된 이메일입니다.', en: 'Email already verified.' }) });
      } else if (result.verificationUrl) {
        // In dev EXPOSE_AUTH_LINKS=true returns the link inline.
        setStatus({
          tone: 'ok',
          text: t({
            ko: `인증 링크가 발급되었습니다. 개발용 링크: ${result.verificationUrl}`,
            en: `Verification link issued. Dev link: ${result.verificationUrl}`,
          }),
        });
      } else {
        setStatus({ tone: 'ok', text: t({ ko: '인증 이메일이 전송되었습니다.', en: 'Verification email sent.' }) });
      }
    } catch (err) {
      setStatus({
        tone: 'err',
        text: err instanceof Error ? err.message : t({ ko: '전송에 실패했습니다.', en: 'Failed to send.' }),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="account-card">
      <h2>{t({ ko: '이메일 인증', en: 'Email verification' })}</h2>
      <p className={`status ${verified ? 'ok' : 'err'}`}>
        {verified
          ? t({ ko: '이메일이 인증되었습니다.', en: 'Your email is verified.' })
          : t({ ko: '이메일이 아직 인증되지 않았습니다.', en: 'Email not yet verified.' })}
      </p>
      {!verified ? (
        <div className="card-actions">
          <button type="button" className="cta-secondary" onClick={() => void resend()} disabled={busy}>
            {busy
              ? t({ ko: '전송 중…', en: 'Sending…' })
              : t({ ko: '인증 링크 보내기', en: 'Send verification link' })}
          </button>
        </div>
      ) : null}
      <StatusLine status={status} />
    </article>
  );
}

function PasswordCard(): JSX.Element {
  const t = useT();
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ tone: Tone; text: string } | null>(null);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setStatus(null);
    if (newPassword !== confirm) {
      setStatus({ tone: 'err', text: t({ ko: '새 비밀번호가 일치하지 않습니다.', en: 'New passwords do not match.' }) });
      return;
    }
    if (newPassword.length < 8) {
      setStatus({ tone: 'err', text: t({ ko: '최소 8자 이상 사용하세요.', en: 'Use at least 8 characters.' }) });
      return;
    }
    setBusy(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      setCurrent('');
      setNext('');
      setConfirm('');
      setStatus({
        tone: 'ok',
        text: t({
          ko: '비밀번호가 변경되었습니다. 다른 기기에서는 로그아웃되었습니다.',
          en: 'Password changed. Other devices were signed out.',
        }),
      });
    } catch (err) {
      const message =
        err instanceof ApiError && err.status === 403
          ? t({ ko: '현재 비밀번호가 올바르지 않습니다.', en: 'Current password is incorrect.' })
          : err instanceof Error
            ? err.message
            : t({ ko: '변경에 실패했습니다.', en: 'Change failed.' });
      setStatus({ tone: 'err', text: message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="account-card">
      <h2>{t({ ko: '비밀번호', en: 'Password' })}</h2>
      <form onSubmit={submit}>
        <label>
          {t({ ko: '현재 비밀번호', en: 'Current password' })}
          <input
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrent(e.target.value)}
            required
            minLength={1}
            maxLength={200}
          />
        </label>
        <label>
          {t({ ko: '새 비밀번호', en: 'New password' })}
          <input
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNext(e.target.value)}
            required
            minLength={8}
            maxLength={200}
          />
        </label>
        <label>
          {t({ ko: '새 비밀번호 확인', en: 'Confirm new password' })}
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={8}
            maxLength={200}
          />
        </label>
        <div className="card-actions">
          <button type="submit" className="cta-primary" disabled={busy}>
            {busy ? t({ ko: '업데이트 중…', en: 'Updating…' }) : t({ ko: '비밀번호 변경', en: 'Update password' })}
          </button>
        </div>
        <StatusLine status={status} />
      </form>
    </article>
  );
}

function DangerCard({ onDeleted }: { onDeleted: () => void }): JSX.Element {
  const t = useT();
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ tone: Tone; text: string } | null>(null);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setStatus(null);
    if (confirmText !== 'DELETE') {
      setStatus({ tone: 'err', text: t({ ko: '확인하려면 DELETE를 입력하세요.', en: 'Type DELETE to confirm.' }) });
      return;
    }
    if (
      !window.confirm(
        t({
          ko: '계정과 업로드된 모든 문서가 영구적으로 삭제됩니다. 계속하시겠습니까?',
          en: 'This will permanently delete your account and all uploaded documents. Continue?',
        }),
      )
    )
      return;
    setBusy(true);
    try {
      await api.deleteAccount(password);
      onDeleted();
    } catch (err) {
      const message =
        err instanceof ApiError && err.status === 403
          ? t({ ko: '비밀번호가 올바르지 않습니다.', en: 'Password is incorrect.' })
          : err instanceof Error
            ? err.message
            : t({ ko: '삭제에 실패했습니다.', en: 'Deletion failed.' });
      setStatus({ tone: 'err', text: message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="account-card danger">
      <h2>{t({ ko: '계정 삭제', en: 'Delete account' })}</h2>
      <p className="muted">
        {t({
          ko: '계정, 업로드된 모든 문서, 공유 링크, 추적된 이벤트를 삭제합니다. 이 작업은 되돌릴 수 없습니다.',
          en: 'Deletes your account, all uploaded documents, share links, and tracked events. This action cannot be undone.',
        })}
      </p>
      <form onSubmit={submit}>
        <label>
          {t({ ko: '비밀번호', en: 'Password' })}
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        <label>
          {t({ ko: '확인하려면 ', en: 'Type ' })}
          <code>DELETE</code>
          {t({ ko: ' 입력', en: ' to confirm' })}
          <input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            required
          />
        </label>
        <div className="card-actions">
          <button type="submit" className="cta-danger" disabled={busy}>
            {busy
              ? t({ ko: '삭제 중…', en: 'Deleting…' })
              : t({ ko: '계정 영구 삭제', en: 'Delete account permanently' })}
          </button>
        </div>
        <StatusLine status={status} />
      </form>
    </article>
  );
}
